import React, { memo, useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { Chart as ChartJS, ChartOptions, Plugin } from 'chart.js';

import * as C from './Charts.styled';
import {
  CHART_GRID,
  CHART_INK,
  CHART_NO_ANIMATION,
  CHART_TICK_FONT,
  chartBaseTooltip,
  chartFaded,
  chartLegend,
} from './chartTokens';
import { registerCharts } from './registerCharts';

registerCharts();

export interface IBoxPlotBox {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

export interface IBoxPlotSeries {
  label: string;
  /** По подписям; `null` — ящика нет. */
  boxes: ReadonlyArray<IBoxPlotBox | null>;
  color: string;
}

export interface IBoxPlotDataset {
  label: string;
  /** Плавающий столбец [q1, q3]; `null` — пусто. */
  data: Array<[number, number] | null>;
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  borderSkipped: false;
  maxBarThickness: number;
}

/**
 * Ящики — плавающими столбцами chart.js от q1 до q3 (без плагина
 * `chartjs-chart-box-and-violin-plot`: новых зависимостей не ставим); усы
 * min–max и медиану дорисовывает `boxPlotWhiskers`.
 */
export const boxPlotData = (
  labels: ReadonlyArray<string>,
  series: ReadonlyArray<IBoxPlotSeries>,
): { labels: string[]; datasets: IBoxPlotDataset[] } => ({
  labels: [...labels],
  datasets: series.map((item) => ({
    label: item.label,
    data: labels.map((_, index) => {
      const box = item.boxes[index];
      return box ? [box.q1, box.q3] : null;
    }),
    backgroundColor: chartFaded(item.color, 0.3),
    borderColor: item.color,
    borderWidth: 1.5,
    borderSkipped: false,
    maxBarThickness: 36,
  })),
});

/** Числовой диапазон всех ящиков (оси): от min до max с запасом 5 %. */
export const boxPlotRange = (
  series: ReadonlyArray<IBoxPlotSeries>,
): { min: number; max: number } | null => {
  const boxes = series.flatMap((item) =>
    item.boxes.filter((box): box is IBoxPlotBox => box !== null),
  );
  if (!boxes.length) return null;
  const min = Math.min(...boxes.map((box) => box.min));
  const max = Math.max(...boxes.map((box) => box.max));
  const pad = (max - min) * 0.05 || Math.abs(max) * 0.05 || 1;
  return { min: min - pad, max: max + pad };
};

const boxPlotWhiskers = (
  read: () => ReadonlyArray<IBoxPlotSeries>,
): Plugin<'bar'> => ({
  id: 'erpBoxPlotWhiskers',
  afterDatasetsDraw: (chart: ChartJS<'bar'>) => {
    const series = read();
    const { ctx } = chart;
    const scale = chart.scales.y;
    if (!scale) return;
    ctx.save();
    series.forEach((item, datasetIndex) => {
      if (!chart.isDatasetVisible(datasetIndex)) return;
      const meta = chart.getDatasetMeta(datasetIndex);
      item.boxes.forEach((box, index) => {
        const element = (meta.data[index] as unknown) as
          | { x: number; width: number }
          | undefined;
        if (!box || !element || !Number.isFinite(element.x)) return;
        const half = element.width / 2;
        const quarter = element.width / 4;
        const y = (value: number) => scale.getPixelForValue(value);
        ctx.strokeStyle = item.color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        // Усы: от ящика до min и до max, с поперечинами.
        ctx.moveTo(element.x, y(box.q3));
        ctx.lineTo(element.x, y(box.max));
        ctx.moveTo(element.x - quarter, y(box.max));
        ctx.lineTo(element.x + quarter, y(box.max));
        ctx.moveTo(element.x, y(box.q1));
        ctx.lineTo(element.x, y(box.min));
        ctx.moveTo(element.x - quarter, y(box.min));
        ctx.lineTo(element.x + quarter, y(box.min));
        ctx.stroke();
        // Медиана — жирной чертой поперёк ящика.
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(element.x - half, y(box.median));
        ctx.lineTo(element.x + half, y(box.median));
        ctx.stroke();
      });
    });
    ctx.restore();
  },
});

export interface IBoxPlotChartProps {
  labels: ReadonlyArray<string>;
  series: ReadonlyArray<IBoxPlotSeries>;
  format: (value: number) => string;
  /** Подписи пяти чисел в подсказке (`мин`, `Q1`, `медиана`, `Q3`, `макс`). */
  statLabels: {
    min: string;
    q1: string;
    median: string;
    q3: string;
    max: string;
  };
  ariaLabel?: string;
}

/**
 * ТЗ 0113 — ящик с усами: ящик q1–q3, медиана чертой, усы до min и max.
 * Подсказка — все пять чисел словами.
 */
export const BoxPlotChart = memo(
  ({ labels, series, format, statLabels, ariaLabel }: IBoxPlotChartProps) => {
    const data = useMemo(() => boxPlotData(labels, series), [labels, series]);
    // Плагин читает ряды по ссылке: react-chartjs-2 берёт плагины только при
    // создании графика.
    const current = useRef(series);
    current.current = series;
    const plugins = useMemo(() => [boxPlotWhiskers(() => current.current)], []);
    const range = useMemo(() => boxPlotRange(series), [series]);
    const options: ChartOptions<'bar'> = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_NO_ANIMATION,
        plugins: {
          legend: { ...chartLegend, display: series.length > 1 },
          tooltip: {
            ...chartBaseTooltip,
            callbacks: {
              label: (item) => {
                const box = series[item.datasetIndex]?.boxes[item.dataIndex];
                if (!box) return '';
                return [
                  `${item.dataset.label}:`,
                  `${statLabels.max} ${format(box.max)}`,
                  `${statLabels.q3} ${format(box.q3)}`,
                  `${statLabels.median} ${format(box.median)}`,
                  `${statLabels.q1} ${format(box.q1)}`,
                  `${statLabels.min} ${format(box.min)}`,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: CHART_TICK_FONT, color: CHART_INK },
          },
          y: {
            min: range?.min,
            max: range?.max,
            grid: { color: CHART_GRID },
            ticks: {
              font: CHART_TICK_FONT,
              color: CHART_INK,
              callback: (value) => format(Number(value)),
            },
          },
        },
      }),
      [series, format, statLabels, range],
    );
    return (
      <C.ChartBox role="img" aria-label={ariaLabel}>
        <Chart
          type="bar"
          data={data as never}
          options={options as never}
          plugins={plugins as never}
        />
      </C.ChartBox>
    );
  },
);

BoxPlotChart.displayName = 'BoxPlotChart';
